import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { Plus, ChevronDown, X, User, DollarSign } from 'lucide-react';
import TipTapEditor from './TipTapEditor';
import api from '../services/api';
import {
    getCachedTiposServicioGrouped,
    getCachedPersonal,
    getCachedCostosFijos,
    getCachedCostosFijosGrouped,
    getCachedCategorias,
    invalidateTiposServicio,
    invalidatePlantillas
} from '../services/cachedCatalogs';
import { safeId, moneyFmt, totalConRentabilidad, gananciaConRentabilidad } from '../utils/helpers';
import { selectStyles } from '../utils/adminShared';
import { Modal, Button, Card, Input, EmptyState, Tooltip } from './ui';

const PlantillaForm = ({ configuracion }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = Boolean(id);
    const hasFetched = useRef(false);

    // Estado principal
    const [plantilla, setPlantilla] = useState({
        nombre: '',
        descripcion: '',
        categoria_id: null,
        activo: true
    });
    const [servicios, setServicios] = useState([]);
    const [extras, setExtras] = useState([]);
    const [servicioExpandido, setServicioExpandido] = useState(null);

    // Catálogos
    const [tiposServicioGrouped, setTiposServicioGrouped] = useState([]);
    const [personalList, setPersonalList] = useState([]);
    const [costosFijosList, setCostosFijosList] = useState([]);
    const [costosFijosGrouped, setCostosFijosGrouped] = useState([]);
    const [categoriasList, setCategoriasList] = useState([]); // para mini modal "crear servicio rapido"
    const [categoriasPlantillaList, setCategoriasPlantillaList] = useState([]); // categorías de plantilla (WEB - Ecommerce, etc.)

    // Mini modal "crear servicio rapido" desde el select de servicio.
    // servicioRowId: el _id de la fila donde se selecciona el nuevo tipo creado.
    const [modalCrearServicio, setModalCrearServicio] = useState({
        open: false,
        servicioRowId: null,
        nombre: '',
        categoria_id: null,
        guardando: false
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Valores de configuración
    const rentabilidad = parseFloat(configuracion?.rentabilidad) || 0.68;
    const rentCostoFijo = parseFloat(configuracion?.rentabilidad_costo_fijo) || 0.68;

    // Cargar datos iniciales
    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;
        cargarDatos();
    }, [id]);

    const cargarDatos = async () => {
        try {
            // Cargar catalogos via cache (TTL 5 min). Instantaneo si ya estan en memoria.
            // Las categorias de plantilla se cargan sin cache (lista corta, raramente cambia).
            const [tiposRes, personalRes, cfRes, cfGroupedRes, categoriasRes, catPlantillaRes] = await Promise.all([
                getCachedTiposServicioGrouped(),
                getCachedPersonal(),
                getCachedCostosFijos(),
                getCachedCostosFijosGrouped(),
                getCachedCategorias(),
                api.plantillas.getCategorias()
            ]);

            setTiposServicioGrouped(tiposRes.data || []);
            setPersonalList(personalRes.data || []);
            setCostosFijosList(cfRes.data || []);
            setCostosFijosGrouped(cfGroupedRes.data || []);
            setCategoriasList(categoriasRes.data || []);
            setCategoriasPlantillaList((catPlantillaRes.data || []).filter(c => c.activo));

            if (isEditing) {
                await cargarPlantilla();
            } else {
                // Nueva plantilla: agregar un servicio vacío
                const nuevoServicio = crearServicioVacio();
                setServicios([nuevoServicio]);
                setServicioExpandido(nuevoServicio._id);
                setLoading(false);
            }
        } catch (err) {
            console.error('Error cargando datos:', err);
            const status = err?.status;
            const msg = err?.message || '';
            if (status === 404 || msg.toLowerCase().includes('no encontrada') || msg.toLowerCase().includes('not found')) {
                navigate('/404', { replace: true });
            } else {
                toast.error('Error al cargar datos');
            }
            setLoading(false);
        }
    };

    const cargarPlantilla = async () => {
        try {
            const response = await api.plantillas.getById(id);
            const data = response.data;

            setPlantilla({
                nombre: data.nombre || '',
                descripcion: data.descripcion || '',
                categoria_id: data.categoria_id || null,
                activo: Boolean(data.activo)
            });

            const serviciosMapped = (data.servicios || []).map(s => ({
                _id: safeId(),
                id: s.id,
                tipo_servicio_id: s.tipo_servicio_id,
                tipo_servicio_nombre: s.tipo_servicio_nombre,
                detalle: s.detalle || '',
                asignaciones: (s.asignaciones || []).map(a => ({
                    _id: safeId(),
                    id: a.id,
                    personal_id: a.personal_id,
                    personal_nombre: a.personal_nombre,
                    horas: parseFloat(a.horas) || 0,
                    descripcion: a.descripcion || '',
                    costo_hora: parseFloat(a.costo_hora) || 0,
                    rentabilidad: a.rentabilidad !== null ? parseFloat(a.rentabilidad) : rentabilidad
                })),
                costosFijos: (s.costosFijos || []).map(cf => ({
                    _id: safeId(),
                    id: cf.id,
                    costo_fijo_id: cf.costo_fijo_id,
                    costo_fijo_nombre: cf.costo_fijo_nombre,
                    categoria_nombre: cf.categoria_nombre,
                    costo: parseFloat(cf.costo) || 0,
                    descripcion: cf.descripcion || '',
                    rentabilidad: cf.rentabilidad !== null ? parseFloat(cf.rentabilidad) : rentCostoFijo
                }))
            }));

            if (serviciosMapped.length > 0) {
                setServicios(serviciosMapped);
                setServicioExpandido(serviciosMapped[0]._id);
            } else {
                const nuevoServicio = crearServicioVacio();
                setServicios([nuevoServicio]);
                setServicioExpandido(nuevoServicio._id);
            }

            setExtras((data.extras || []).map(e => ({
                _id: safeId(),
                id: e.id,
                detalle: e.detalle || '',
                monto: parseFloat(e.monto) || 0
            })));
        } catch (err) {
            console.error('Error cargando plantilla:', err);
            toast.error('Error al cargar plantilla');
            navigate('/plantillas');
        } finally {
            setLoading(false);
        }
    };

    const crearServicioVacio = () => ({
        _id: safeId(),
        tipo_servicio_id: null,
        tipo_servicio_nombre: '',
        detalle: '',
        asignaciones: [],
        costosFijos: []
    });

    // === Handlers de Servicios ===
    const agregarServicio = () => {
        const nuevo = crearServicioVacio();
        setServicios([...servicios, nuevo]);
        setServicioExpandido(nuevo._id);
    };

    const eliminarServicio = (servicioId) => {
        if (servicios.length <= 1) {
            toast.error('Debe haber al menos un servicio');
            return;
        }
        const nuevos = servicios.filter(s => s._id !== servicioId);
        setServicios(nuevos);
        if (servicioExpandido === servicioId && nuevos.length > 0) {
            setServicioExpandido(nuevos[0]._id);
        }
    };

    const actualizarServicio = (servicioId, campo, valor) => {
        setServicios(servicios.map(s => {
            if (s._id !== servicioId) return s;

            if (campo === 'tipo_servicio_id') {
                const tipo = tiposServicioGrouped.flatMap(g => g.options).find(o => o.value === valor);
                return {
                    ...s,
                    tipo_servicio_id: valor,
                    tipo_servicio_nombre: tipo?.label || ''
                };
            }
            return { ...s, [campo]: valor };
        }));
    };

    // Abre el mini-modal "crear servicio rapido" desde el noOptionsMessage del Select.
    // Prellena el nombre con el inputValue que el usuario tipeo.
    const abrirCrearServicio = (servicioRowId, nombreTipeado) => {
        setModalCrearServicio({
            open: true,
            servicioRowId,
            nombre: (nombreTipeado || '').trim(),
            categoria_id: null,
            guardando: false
        });
    };

    const cerrarCrearServicio = () => {
        setModalCrearServicio({
            open: false,
            servicioRowId: null,
            nombre: '',
            categoria_id: null,
            guardando: false
        });
    };

    const guardarNuevoServicio = async () => {
        const nombre = modalCrearServicio.nombre.trim();
        if (!nombre) {
            toast.error('El nombre del servicio es obligatorio');
            return;
        }
        if (!modalCrearServicio.categoria_id) {
            toast.error('La categoría es obligatoria');
            return;
        }
        setModalCrearServicio(m => ({ ...m, guardando: true }));
        try {
            const res = await api.tiposServicio.create({
                nombre,
                categoria_id: modalCrearServicio.categoria_id,
                descripcion: ''
            });
            const nuevoId = res.data?.id;

            // Invalidar cache para que otros consumidores vean el nuevo servicio.
            invalidateTiposServicio();

            // Recargar lista agrupada para que el nuevo aparezca aqui mismo.
            const tiposRes = await getCachedTiposServicioGrouped();
            setTiposServicioGrouped(tiposRes.data || []);

            // Autoseleccionar el nuevo servicio en la fila que lo origino.
            if (nuevoId && modalCrearServicio.servicioRowId) {
                actualizarServicio(modalCrearServicio.servicioRowId, 'tipo_servicio_id', nuevoId);
            }

            toast.success(`Servicio "${nombre}" creado y seleccionado`);
            cerrarCrearServicio();
        } catch (err) {
            toast.error('Error al crear el servicio: ' + (err.message || ''));
            setModalCrearServicio(m => ({ ...m, guardando: false }));
        }
    };

    // === Handlers de Asignaciones ===
    const agregarAsignacion = (servicioId) => {
        setServicios(servicios.map(s => {
            if (s._id !== servicioId) return s;
            return {
                ...s,
                asignaciones: [...s.asignaciones, {
                    _id: safeId(),
                    personal_id: null,
                    personal_nombre: '',
                    horas: 0,
                    descripcion: '',
                    costo_hora: 0,
                    rentabilidad: rentabilidad
                }]
            };
        }));
    };

    const eliminarAsignacion = (servicioId, asigId) => {
        setServicios(servicios.map(s => {
            if (s._id !== servicioId) return s;
            return {
                ...s,
                asignaciones: s.asignaciones.filter(a => a._id !== asigId)
            };
        }));
    };

    const actualizarAsignacion = (servicioId, asigId, campo, valor) => {
        setServicios(servicios.map(s => {
            if (s._id !== servicioId) return s;
            return {
                ...s,
                asignaciones: s.asignaciones.map(a => {
                    if (a._id !== asigId) return a;

                    if (campo === 'personal_id' && valor) {
                        const personal = personalList.find(p => p.id === valor);
                        if (personal) {
                            return {
                                ...a,
                                personal_id: valor,
                                personal_nombre: personal.nombre,
                                costo_hora: parseFloat(personal.costo_hora) || 0
                            };
                        }
                    }
                    return { ...a, [campo]: campo === 'horas' || campo === 'rentabilidad' ? (parseFloat(valor) || 0) : valor };
                })
            };
        }));
    };

    // === Handlers de Costos Fijos ===
    const agregarCostoFijo = (servicioId) => {
        setServicios(servicios.map(s => {
            if (s._id !== servicioId) return s;
            return {
                ...s,
                costosFijos: [...s.costosFijos, {
                    _id: safeId(),
                    costo_fijo_id: null,
                    costo_fijo_nombre: '',
                    categoria_nombre: '',
                    costo: 0,
                    descripcion: '',
                    rentabilidad: rentCostoFijo
                }]
            };
        }));
    };

    const eliminarCostoFijo = (servicioId, cfId) => {
        setServicios(servicios.map(s => {
            if (s._id !== servicioId) return s;
            return {
                ...s,
                costosFijos: s.costosFijos.filter(cf => cf._id !== cfId)
            };
        }));
    };

    const actualizarCostoFijo = (servicioId, cfId, campo, valor) => {
        setServicios(servicios.map(s => {
            if (s._id !== servicioId) return s;
            return {
                ...s,
                costosFijos: s.costosFijos.map(cf => {
                    if (cf._id !== cfId) return cf;

                    if (campo === 'costo_fijo_id' && valor) {
                        const costoFijo = costosFijosList.find(c => c.id === valor);
                        if (costoFijo) {
                            return {
                                ...cf,
                                costo_fijo_id: valor,
                                costo_fijo_nombre: costoFijo.nombre,
                                categoria_nombre: costoFijo.categoria_nombre || '',
                                costo: parseFloat(costoFijo.costo) || 0,
                                rentabilidad: costoFijo.rentabilidad !== null ? parseFloat(costoFijo.rentabilidad) : rentCostoFijo
                            };
                        }
                    }
                    return { ...cf, [campo]: campo === 'costo' || campo === 'rentabilidad' ? (parseFloat(valor) || 0) : valor };
                })
            };
        }));
    };

    // === Handlers de Extras ===
    const agregarExtra = () => {
        setExtras([...extras, { _id: safeId(), detalle: '', monto: 0 }]);
    };

    const eliminarExtra = (extraId) => {
        setExtras(extras.filter(e => e._id !== extraId));
    };

    const actualizarExtra = (extraId, campo, valor) => {
        setExtras(extras.map(e =>
            e._id === extraId ? { ...e, [campo]: campo === 'monto' ? (parseFloat(valor) || 0) : valor } : e
        ));
    };

    // === Guardar ===
    const handleGuardar = async () => {
        if (!plantilla.nombre.trim()) {
            toast.error('Ingrese un nombre para la plantilla');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                ...plantilla,
                servicios: servicios.map((s, idx) => ({
                    id: s.id,
                    tipo_servicio_id: s.tipo_servicio_id,
                    tipo_servicio_nombre: s.tipo_servicio_nombre,
                    detalle: s.detalle,
                    orden: idx,
                    asignaciones: s.asignaciones.map(a => ({
                        id: a.id,
                        personal_id: a.personal_id,
                        personal_nombre: a.personal_nombre,
                        horas: a.horas,
                        descripcion: a.descripcion || '',
                        costo_hora: a.costo_hora,
                        rentabilidad: a.rentabilidad
                    })),
                    costosFijos: s.costosFijos.map(cf => ({
                        id: cf.id,
                        costo_fijo_id: cf.costo_fijo_id,
                        costo_fijo_nombre: cf.costo_fijo_nombre,
                        categoria_nombre: cf.categoria_nombre,
                        costo: cf.costo,
                        descripcion: cf.descripcion || '',
                        rentabilidad: cf.rentabilidad
                    }))
                })),
                extras: extras.map((e, idx) => ({
                    id: e.id,
                    detalle: e.detalle,
                    monto: e.monto,
                    orden: idx
                }))
            };

            if (isEditing) {
                await api.plantillas.update(id, payload);
                toast.success('Plantilla actualizada');
            } else {
                await api.plantillas.create(payload);
                toast.success('Plantilla creada');
            }
            invalidatePlantillas();
            navigate('/plantillas');
        } catch (err) {
            console.error('Error guardando plantilla:', err);
            toast.error('Error al guardar plantilla');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="loading">Cargando...</div>;
    }

    return (
        <div className="cotizacion-form">
            {/* Header */}
            <Card>
                <Card.Header>
                    <h3 style={{ margin: 0 }}>{isEditing ? 'Editar Plantilla' : 'Nueva Plantilla'}</h3>
                </Card.Header>
                <Card.Body>
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-4 items-end">
                        <Input
                            id="plantilla-nombre"
                            label="Nombre de la plantilla"
                            required
                            type="text"
                            value={plantilla.nombre}
                            onChange={(e) => setPlantilla({ ...plantilla, nombre: e.target.value })}
                            placeholder="Ej: Cotización estándar de video"
                        />
                        {/* Select de categoría */}
                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="plantilla-categoria" className="text-xs font-medium text-muted">
                                Categoría
                            </label>
                            <Select
                                inputId="plantilla-categoria"
                                styles={selectStyles}
                                options={categoriasPlantillaList.map(c => ({ value: c.id, label: c.nombre, data: c }))}
                                value={
                                    plantilla.categoria_id
                                        ? (() => {
                                            const c = categoriasPlantillaList.find(x => x.id === plantilla.categoria_id);
                                            return c ? { value: c.id, label: c.nombre, data: c } : null;
                                        })()
                                        : null
                                }
                                onChange={(selected) => setPlantilla({ ...plantilla, categoria_id: selected?.value || null })}
                                placeholder="Sin categoría"
                                isClearable
                                isSearchable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>
                        {/* Estado: switch consistente con el resto del form */}
                        <div className="flex flex-col gap-1.5">
                            <span className="text-xs font-medium text-muted">Estado</span>
                            <label className="switch-container">
                                <span className="switch">
                                    <input
                                        type="checkbox"
                                        checked={plantilla.activo}
                                        onChange={(e) => setPlantilla({ ...plantilla, activo: e.target.checked })}
                                    />
                                    <span className="switch-slider"></span>
                                </span>
                                <span className={`switch-label ${plantilla.activo ? 'active' : ''}`}>
                                    {plantilla.activo ? 'Activo' : 'Inactivo'}
                                </span>
                            </label>
                        </div>
                    </div>

                    <div className="flex flex-col gap-1.5 mt-4">
                        <label htmlFor="plantilla-descripcion" className="text-xs font-medium text-muted">
                            Descripción (opcional)
                        </label>
                        <textarea
                            id="plantilla-descripcion"
                            className="bg-surface border border-line rounded-lg px-3 py-2 text-sm text-text outline-none focus:ring-2 focus:ring-cyan2/30 focus:border-cyan2 placeholder:text-muted/70 transition"
                            rows={2}
                            value={plantilla.descripcion}
                            onChange={(e) => setPlantilla({ ...plantilla, descripcion: e.target.value })}
                            placeholder="Breve descripción de cuándo usar esta plantilla"
                        />
                    </div>
                </Card.Body>
            </Card>

            {/* Servicios */}
            <Card className="mt-3.5">
                <Card.Header>
                    <h3 style={{ margin: 0 }}>Servicios</h3>
                    <Button
                        variant="primary"
                        size="sm"
                        leftIcon={<Plus size={14} />}
                        onClick={agregarServicio}
                    >
                        Agregar servicio
                    </Button>
                </Card.Header>
                <Card.Body>

                <div className="servicios-list">
                    {servicios.map((servicio, idx) => {
                        const isExpanded = servicioExpandido === servicio._id;
                        const toggleExpand = () => setServicioExpandido(isExpanded ? null : servicio._id);

                        return (
                            <div key={servicio._id} className={`servicio-item ${isExpanded ? 'expanded' : 'collapsed'}`}>
                                <div className="servicio-header" onClick={toggleExpand}>
                                    <div className="servicio-header-left">
                                        <span className="servicio-number">#{idx + 1}</span>
                                        <div className="select-wrapper" onClick={(e) => {
                                            e.stopPropagation();
                                            if (!isExpanded) setServicioExpandido(servicio._id);
                                        }}>
                                            <Select
                                                styles={selectStyles}
                                                options={tiposServicioGrouped}
                                                value={
                                                    tiposServicioGrouped
                                                        .flatMap(g => g.options)
                                                        .find(opt => opt.value === servicio.tipo_servicio_id) || null
                                                }
                                                onChange={(selected) => actualizarServicio(servicio._id, 'tipo_servicio_id', selected?.value || null)}
                                                placeholder="Seleccionar servicio..."
                                                isClearable
                                                isSearchable
                                                isDisabled={!isExpanded}
                                                noOptionsMessage={({ inputValue }) => (
                                                    inputValue ? (
                                                        <button
                                                            type="button"
                                                            className="select-create-btn"
                                                            onMouseDown={(e) => {
                                                                // mouseDown antes del blur del select cierra el menu
                                                                e.preventDefault();
                                                                abrirCrearServicio(servicio._id, inputValue);
                                                            }}
                                                        >
                                                            <Plus size={14} aria-hidden="true" />
                                                            Crear servicio "<strong>{inputValue}</strong>"
                                                        </button>
                                                    ) : 'Sin opciones'
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="servicio-header-right">
                                        <button
                                            className={`toggle-icon ${isExpanded ? 'rotated' : ''}`}
                                            type="button"
                                            aria-expanded={isExpanded}
                                            aria-label={isExpanded ? 'Colapsar servicio' : 'Expandir servicio'}
                                            onClick={(e) => { e.stopPropagation(); toggleExpand(); }}
                                        >
                                            <ChevronDown size={20} aria-hidden="true" />
                                        </button>
                                        <Tooltip content="Eliminar servicio">
                                            <button
                                                className="btn-icon delete"
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); eliminarServicio(servicio._id); }}
                                                aria-label="Eliminar servicio"
                                            >
                                                <X size={18} aria-hidden="true" />
                                            </button>
                                        </Tooltip>
                                    </div>
                                </div>

                                <div className={`servicio-content ${isExpanded ? 'open' : ''}`}>
                                    <div className="servicio-content-inner">
                                        <div className="servicio-detalle">
                                            <div className="field">
                                                <span className="label">Detalle del servicio</span>
                                                <TipTapEditor
                                                    value={servicio.detalle}
                                                    onChange={(html) => actualizarServicio(servicio._id, 'detalle', html)}
                                                    placeholder="Descripción detallada del servicio..."
                                                />
                                            </div>
                                        </div>

                                        {/* Sección de Costos de Servicio */}
                                        <div className="costos-servicio-section">
                                            {/* Divisor PERSONAL con boton agregar a la derecha */}
                                            <div className="costos-divider personal-divider">
                                                <h3 className="costos-divider-title">
                                                    <User size={16} aria-hidden="true" />
                                                    <span>Personal</span>
                                                </h3>
                                                <button
                                                    className="btn-add btn-add-personal"
                                                    type="button"
                                                    onClick={() => agregarAsignacion(servicio._id)}
                                                >
                                                    <Plus size={14} aria-hidden="true" />
                                                    Agregar personal
                                                </button>
                                            </div>

                                            {/* Tabla de Personal — diseño por zonas (cotizacion) con solo
                                                los campos que aplican a plantilla (sin 12% adic, sin subtotal,
                                                sin ganancia, sin total). */}
                                            {servicio.asignaciones.length > 0 && (
                                                <div className="asignaciones-table">
                                                    <div className="asignaciones-row asignaciones-header-row">
                                                        <div className="asig-zone asig-zone-quien">
                                                            <div className="asig-col asig-personal">Personal</div>
                                                        </div>
                                                        <span className="asig-zone-divider" aria-hidden="true" />
                                                        <div className="asig-zone asig-zone-cuanto">
                                                            <div className="asig-col asig-horas">Horas</div>
                                                            <div className="asig-col asig-costo">Costo/hr</div>
                                                        </div>
                                                        <span className="asig-zone-divider" aria-hidden="true" />
                                                        <div className="asig-zone asig-zone-resultado">
                                                            <Tooltip content="Margen de rentabilidad aplicado al subtotal">
                                                                <div className="asig-col asig-rentabilidad">Rentab.</div>
                                                            </Tooltip>
                                                        </div>
                                                        <div className="asig-col asig-actions"></div>
                                                    </div>

                                                    {servicio.asignaciones.map((asig) => (
                                                        <div key={asig._id} className="asignaciones-row">
                                                            <div className="asig-zone asig-zone-quien">
                                                                <div className="asig-col asig-personal">
                                                                    <Select
                                                                        styles={selectStyles}
                                                                        options={personalList.map(p => ({
                                                                            value: p.id,
                                                                            label: p.nombre
                                                                        }))}
                                                                        value={
                                                                            asig.personal_id
                                                                                ? {
                                                                                    value: asig.personal_id,
                                                                                    label: (() => {
                                                                                        const p = personalList.find(p => p.id === asig.personal_id);
                                                                                        return p ? p.nombre : '';
                                                                                    })()
                                                                                }
                                                                                : null
                                                                        }
                                                                        onChange={(selected) => actualizarAsignacion(servicio._id, asig._id, 'personal_id', selected?.value || null)}
                                                                        placeholder="Seleccionar..."
                                                                        isClearable
                                                                        isSearchable
                                                                        menuPortalTarget={document.body}
                                                                        menuPosition="fixed"
                                                                    />
                                                                </div>
                                                                <input
                                                                    className="input input-sm asig-desc-input"
                                                                    type="text"
                                                                    maxLength={255}
                                                                    placeholder="Tarea / rol (uso interno · opcional)"
                                                                    value={asig.descripcion || ''}
                                                                    onChange={(e) => actualizarAsignacion(servicio._id, asig._id, 'descripcion', e.target.value)}
                                                                />
                                                            </div>
                                                            <span className="asig-zone-divider" aria-hidden="true" />
                                                            <div className="asig-zone asig-zone-cuanto">
                                                                <div className="asig-col asig-horas">
                                                                    <input
                                                                        className="input input-sm"
                                                                        type="number"
                                                                        min="0"
                                                                        step="0.5"
                                                                        value={asig.horas === '' || asig.horas == null ? '' : Number(asig.horas)}
                                                                        onChange={(e) => actualizarAsignacion(servicio._id, asig._id, 'horas', e.target.value)}
                                                                    />
                                                                </div>
                                                                <div className="asig-col asig-costo">
                                                                    <span className="cost-readonly">
                                                                        {moneyFmt(asig.costo_hora, 'PEN')}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <span className="asig-zone-divider" aria-hidden="true" />
                                                            <div className="asig-zone asig-zone-resultado">
                                                                <div className="asig-col asig-rentabilidad">
                                                                    <div className="rentabilidad-input-wrapper">
                                                                        <input
                                                                            className="input input-sm input-rentabilidad"
                                                                            type="number"
                                                                            min="-99"
                                                                            max="99"
                                                                            step="0.5"
                                                                            value={(() => {
                                                                                const r = asig.rentabilidad ?? rentabilidad;
                                                                                return Number((r * 100).toFixed(2));
                                                                            })()}
                                                                            onChange={(e) => {
                                                                                const raw = parseFloat(e.target.value) || 0;
                                                                                const clamped = Math.max(-99, Math.min(99, raw));
                                                                                actualizarAsignacion(servicio._id, asig._id, 'rentabilidad', clamped / 100);
                                                                            }}
                                                                        />
                                                                        <span className="rentabilidad-percent">%</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="asig-col asig-actions">
                                                                <Tooltip content="Eliminar asignación">
                                                                    <button
                                                                        className="btn-icon delete"
                                                                        type="button"
                                                                        onClick={() => eliminarAsignacion(servicio._id, asig._id)}
                                                                        aria-label="Eliminar asignación"
                                                                    >
                                                                        <X size={16} aria-hidden="true" />
                                                                    </button>
                                                                </Tooltip>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Divisor COSTOS FIJOS con boton agregar a la derecha */}
                                            <div className="costos-divider costos-fijos-divider">
                                                <h3 className="costos-divider-title">
                                                    <DollarSign size={16} aria-hidden="true" />
                                                    <span>Costos fijos</span>
                                                </h3>
                                                <button
                                                    className="btn-add btn-add-costo-fijo"
                                                    type="button"
                                                    onClick={() => agregarCostoFijo(servicio._id)}
                                                >
                                                    <Plus size={14} aria-hidden="true" />
                                                    Agregar costo fijo
                                                </button>
                                            </div>

                                            {/* Tabla de Costos Fijos — diseño por zonas (cotizacion) con solo
                                                los campos que aplican a plantilla (sin subtotal, sin ganancia,
                                                sin total). */}
                                            {servicio.costosFijos.length > 0 && (
                                                <div className="costos-fijos-table">
                                                    <div className="costos-fijos-row costos-fijos-header-row">
                                                        <div className="cf-zone cf-zone-quien">
                                                            <div className="cf-col cf-select">Costo fijo</div>
                                                        </div>
                                                        <span className="cf-zone-divider" aria-hidden="true" />
                                                        <div className="cf-zone cf-zone-cuanto">
                                                            <div className="cf-col cf-costo">Costo</div>
                                                        </div>
                                                        <span className="cf-zone-divider" aria-hidden="true" />
                                                        <div className="cf-zone cf-zone-resultado">
                                                            <Tooltip content="Margen de rentabilidad aplicado al costo">
                                                                <div className="cf-col cf-rentabilidad">Rentab.</div>
                                                            </Tooltip>
                                                        </div>
                                                        <div className="cf-col cf-actions"></div>
                                                    </div>

                                                    {servicio.costosFijos.map((cf) => (
                                                        <div key={cf._id} className="costos-fijos-row">
                                                            <div className="cf-zone cf-zone-quien">
                                                                <div className="cf-col cf-select">
                                                                    <Select
                                                                        styles={selectStyles}
                                                                        options={costosFijosGrouped}
                                                                        value={
                                                                            cf.costo_fijo_id
                                                                                ? costosFijosGrouped
                                                                                    .flatMap(g => g.options)
                                                                                    .find(opt => opt.value === cf.costo_fijo_id) || { value: cf.costo_fijo_id, label: cf.costo_fijo_nombre }
                                                                                : null
                                                                        }
                                                                        onChange={(selected) => actualizarCostoFijo(servicio._id, cf._id, 'costo_fijo_id', selected?.value || null)}
                                                                        placeholder="Seleccionar costo fijo..."
                                                                        isSearchable
                                                                        menuPortalTarget={document.body}
                                                                        menuPosition="fixed"
                                                                    />
                                                                </div>
                                                                <input
                                                                    className="input input-sm asig-desc-input"
                                                                    type="text"
                                                                    maxLength={255}
                                                                    placeholder="Descripción / nota (uso interno · opcional)"
                                                                    value={cf.descripcion || ''}
                                                                    onChange={(e) => actualizarCostoFijo(servicio._id, cf._id, 'descripcion', e.target.value)}
                                                                />
                                                            </div>
                                                            <span className="cf-zone-divider" aria-hidden="true" />
                                                            <div className="cf-zone cf-zone-cuanto">
                                                                <div className="cf-col cf-costo">
                                                                    <input
                                                                        className="input input-sm"
                                                                        type="number"
                                                                        min="0"
                                                                        step="0.01"
                                                                        value={cf.costo}
                                                                        onChange={(e) => actualizarCostoFijo(servicio._id, cf._id, 'costo', e.target.value)}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <span className="cf-zone-divider" aria-hidden="true" />
                                                            <div className="cf-zone cf-zone-resultado">
                                                                <div className="cf-col cf-rentabilidad">
                                                                    <div className="rentabilidad-input-wrapper">
                                                                        <input
                                                                            className="input input-sm input-rentabilidad"
                                                                            type="number"
                                                                            min="-99"
                                                                            max="99"
                                                                            step="0.5"
                                                                            value={(() => {
                                                                                const r = cf.rentabilidad ?? rentCostoFijo;
                                                                                return Number((r * 100).toFixed(2));
                                                                            })()}
                                                                            onChange={(e) => {
                                                                                const raw = parseFloat(e.target.value) || 0;
                                                                                const clamped = Math.max(-99, Math.min(99, raw));
                                                                                actualizarCostoFijo(servicio._id, cf._id, 'rentabilidad', clamped / 100);
                                                                            }}
                                                                        />
                                                                        <span className="rentabilidad-percent">%</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="cf-col cf-actions">
                                                                <Tooltip content="Eliminar costo fijo">
                                                                <button
                                                                    className="btn-icon delete"
                                                                    type="button"
                                                                    onClick={() => eliminarCostoFijo(servicio._id, cf._id)}
                                                                    aria-label="Eliminar costo fijo"
                                                                >
                                                                    <X size={16} aria-hidden="true" />
                                                                </button>
                                                                </Tooltip>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                </Card.Body>
            </Card>

            {/* Extras — mismo diseño que CotizacionForm */}
            <Card className="mt-3.5">
                <Card.Header
                    title="Extras (opcional)"
                    actions={
                        <Button
                            variant="primary"
                            onClick={agregarExtra}
                            leftIcon={<Plus size={16} aria-hidden="true" />}
                        >
                            Agregar extra
                        </Button>
                    }
                />
                <Card.Body>
                    {extras.length > 0 && (
                        <div className="extras-list">
                            {extras.map((extra) => (
                                <div key={extra._id} className="extra-item">
                                    <div className="extra-content">
                                        <div className="extra-detalle">
                                            <TipTapEditor
                                                value={extra.detalle}
                                                onChange={(html) => actualizarExtra(extra._id, 'detalle', html)}
                                                placeholder="Descripción del extra..."
                                            />
                                        </div>
                                        <Input
                                            id={`extra-monto-${extra._id}`}
                                            label="Monto"
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={extra.monto}
                                            onChange={(e) => actualizarExtra(extra._id, 'monto', e.target.value)}
                                            wrapperClassName="w-[150px] shrink-0"
                                        />
                                        <Tooltip content="Eliminar extra">
                                            <button
                                                className="btn-icon delete"
                                                type="button"
                                                onClick={() => eliminarExtra(extra._id)}
                                                aria-label="Eliminar extra"
                                            >
                                                <X size={18} aria-hidden="true" />
                                            </button>
                                        </Tooltip>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {extras.length === 0 && (
                        <EmptyState
                            icon={<DollarSign size={28} aria-hidden="true" />}
                            title="Sin extras agregados"
                            description="Agrega costos puntuales que se suman al total: licencias, servicios externos, viáticos, etc."
                            action={
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={agregarExtra}
                                    leftIcon={<Plus size={16} aria-hidden="true" />}
                                >
                                    Agregar extra
                                </Button>
                            }
                            className="py-8"
                        />
                    )}
                </Card.Body>
            </Card>

            {/* Botones de acción */}
            <div className="form-actions-bar">
                <Button
                    variant="ghost"
                    onClick={() => navigate('/plantillas')}
                    disabled={saving}
                >
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    onClick={handleGuardar}
                    disabled={saving}
                >
                    {saving ? 'Guardando…' : 'Guardar Plantilla'}
                </Button>
            </div>

            {/* Mini-modal "crear servicio rapido" desde el select de servicio.
                Cumple ley DESIGN: cierra con Esc + click overlay (ver Modal primitivo). */}
            <Modal
                open={modalCrearServicio.open}
                onClose={modalCrearServicio.guardando ? undefined : cerrarCrearServicio}
                title="Crear servicio"
                size="sm"
                footer={
                    <>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={cerrarCrearServicio}
                            disabled={modalCrearServicio.guardando}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={guardarNuevoServicio}
                            disabled={modalCrearServicio.guardando || !modalCrearServicio.nombre.trim() || !modalCrearServicio.categoria_id}
                        >
                            {modalCrearServicio.guardando ? 'Creando...' : 'Crear servicio'}
                        </Button>
                    </>
                }
            >
                <div className="flex flex-col gap-3">
                    <Input
                        label="Nombre del servicio"
                        value={modalCrearServicio.nombre}
                        onChange={(e) => setModalCrearServicio(m => ({ ...m, nombre: e.target.value }))}
                        placeholder="Ej. Landing institucional"
                        autoFocus
                        disabled={modalCrearServicio.guardando}
                    />
                    <div className="field">
                        <span className="label">Categoría</span>
                        <Select
                            styles={selectStyles}
                            options={categoriasList.map(c => ({ value: c.id, label: c.nombre }))}
                            value={
                                categoriasList
                                    .map(c => ({ value: c.id, label: c.nombre }))
                                    .find(opt => opt.value === modalCrearServicio.categoria_id) || null
                            }
                            onChange={(selected) =>
                                setModalCrearServicio(m => ({ ...m, categoria_id: selected?.value || null }))
                            }
                            placeholder="Seleccionar categoría..."
                            isSearchable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            isDisabled={modalCrearServicio.guardando}
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default PlantillaForm;
